import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

// AnswerBlock — short, self-contained, quotable text placed as the FIRST
// paragraph under each H2 so AI Overviews can lift it verbatim.
const AnswerBlock = ({ children }: { children: ReactNode }) => (
  <div className="not-prose mb-5 rounded-xl border border-accent/30 bg-accent/5 p-4 sm:p-5">
    <p className="text-[1.0625rem] leading-relaxed text-foreground">
      {children}
    </p>
  </div>
);

const a_diff =
  "Standard TMS is one session a day for about six weeks, aimed with a standard scalp position. SAINT\u00ae is ten sessions a day for five days, aimed at a target picked from your own brain MRI by FDA-cleared software, and it's delivered only by providers listed on Magnus Medical's official provider locator.";

const a_accel =
  "No. Some 5-day \u201caccelerated TMS\u201d programs copy SAINT\u00ae's schedule of 50 sessions in five days but skip the brain MRI and use standard machines with an off-label schedule. They are a different treatment, not a lower-priced version of SAINT\u00ae.";

const a_better =
  "No head-to-head trial has compared them. SAINT\u00ae offers a faster five-day schedule and a target personalized from your own brain MRI; standard TMS has a larger evidence base and broader insurance coverage. Suitability depends on your individual circumstances, and individual results vary.";

const faqs: LearnFAQ[] = [
  {
    question: "What is the difference between SAINT and TMS?",
    answer: a_diff,
  },
  {
    question: "Is accelerated TMS the same as SAINT?",
    answer: a_accel,
  },
  {
    question: "Is SAINT better than regular TMS?",
    answer: a_better,
  },
  {
    question: "How long does each treatment take?",
    answer:
      "SAINT\u00ae takes five consecutive days, with ten sessions per day and 50-minute breaks. Standard TMS takes about six weeks, with one session per day, five days a week.",
  },
  {
    question: "Does insurance cover SAINT and traditional TMS?",
    answer:
      "Standard TMS is broadly covered by commercial insurance, Medicare, and the VA when criteria are met. SAINT\u00ae coverage is currently limited and decided case by case through prior authorization. We verify your benefits for free. Self-pay is available.",
  },
  {
    question: "Can I try traditional TMS if SAINT isn't right for me?",
    answer:
      "Yes. Neuro Wellness offers both SAINT\u00ae and traditional TMS. If SAINT\u00ae is not appropriate for you \u2014 due to cost, insurance, or eligibility \u2014 traditional TMS may be an option. Your psychiatrist can help you decide which is right for your situation.",
  },
];

export const pageSaintVsTraditional: LearnPageData = {
  slug: "saint-vs-traditional-tms",
  title: "SAINT\u00ae vs TMS: 7 Key Differences (Schedule, Targeting, Results)",
  description:
    "SAINT\u00ae vs regular TMS and 5-day \u201caccelerated TMS\u201d: how targeting, schedule, device, age limits, results and cost really differ.",
  canonical: "/learn/saint-vs-traditional-tms",
  h1: "SAINT\u00ae vs TMS: What's the Difference?",
  breadcrumbLabel: "SAINT\u00ae vs TMS",
  crumbs: baseCrumbs("SAINT\u00ae vs TMS"),
  shortAnswer:
    "Standard TMS is one session a day for about six weeks, aimed with a standard scalp position. SAINT\u00ae is ten sessions a day for five days, aimed at a target picked from your own brain MRI by FDA-cleared software, and it's delivered only by providers listed on Magnus Medical's official provider locator. This page compares them across schedule, targeting, device, FDA status, age limits, published results, and cost \u2014 and shows where lower-priced 5-day \u201caccelerated TMS\u201d programs fall in between.",
  keyTakeaways: [
    "Standard TMS: ~36 sessions over ~6 weeks, scalp-measurement targeting.",
    "5-day \u201caccelerated TMS\u201d programs: often 50 sessions in 5 days, scalp or head measurements, off-label.",
    "SAINT\u00ae: 50 sessions in 5 days, your own brain MRI + FDA-cleared targeting software, verified providers only.",
    "Only SAINT\u00ae requires a brain MRI; the other two do not.",
    "SAINT\u00ae is FDA-cleared for adults; standard TMS is broadly covered by insurance.",
    "79% remission in the SAINT\u00ae controlled trial \u2014 those results don't transfer to off-label programs.",
  ],

  sections: [
    {
      heading: "What is the difference between SAINT and TMS?",
      body: (
        <>
          <AnswerBlock>{a_diff}</AnswerBlock>
          {P(
            "Standard (traditional) TMS delivers one stimulation session per day, five days a week, for about six weeks \u2014 roughly 36 sessions total. The coil is positioned using a scalp measurement (often the \u201c5.5 cm rule\u201d or motor-threshold-based positioning), which is a population average, not a personalized target.",
          )}
          {P(
            "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy) compresses the course into five consecutive days: ten 10-minute sessions a day with 50-minute breaks, for 50 sessions total. Before treatment, each patient has a brain MRI (a functional MRI, or fMRI). FDA-cleared targeting software uses the scan's functional-connectivity data to pick the exact spot to stimulate for that person \u2014 a personalized target, not a population average.",
          )}
          {P(
            "SAINT\u00ae is delivered on the Magnus Neuromodulation System and only by providers listed on Magnus Medical's official provider locator. Standard TMS is available on a variety of FDA-cleared TMS devices at many clinics.",
          )}
        </>
      ),
    },
    {
      heading:
        "Three-way comparison: Standard TMS vs 5-day \u201caccelerated TMS\u201d vs SAINT\u00ae",
      body: (
        <>
          {P(
            "The table below compares standard TMS, 5-day \u201caccelerated TMS\u201d programs that are not SAINT\u00ae, and SAINT\u00ae side by side. The key takeaway: the schedule can be copied, but the personalized MRI target, the FDA-cleared software, and the published results cannot.",
          )}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="p-3 text-left font-semibold text-foreground">
                    Dimension
                  </th>
                  <th className="p-3 text-left font-semibold text-foreground">
                    Standard TMS
                  </th>
                  <th className="p-3 text-left font-semibold text-foreground">
                    5-day \u201caccelerated TMS\u201d programs (not SAINT\u00ae)
                  </th>
                  <th className="p-3 text-left font-semibold text-accent">
                    SAINT\u00ae
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium text-foreground">Schedule</td>
                  <td className="p-3 text-foreground">
                    ~36 sessions over ~6 weeks
                  </td>
                  <td className="p-3 text-foreground">
                    Often 50 sessions in 5 days
                  </td>
                  <td className="p-3 text-foreground">
                    50 sessions in 5 days (10/day, 50-min breaks)
                  </td>
                </tr>
                <tr className="border-b border-border bg-muted/20">
                  <td className="p-3 font-medium text-foreground">
                    How the target is found
                  </td>
                  <td className="p-3 text-foreground">
                    Scalp measurement / standard position
                  </td>
                  <td className="p-3 text-foreground">
                    Usually scalp or head measurements (for example the
                    \u201cBEAM F3\u201d method)
                  </td>
                  <td className="p-3 text-foreground">
                    Your own functional-connectivity brain MRI + FDA-cleared
                    targeting software
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium text-foreground">
                    MRI required?
                  </td>
                  <td className="p-3 text-foreground">No</td>
                  <td className="p-3 text-foreground">Usually no</td>
                  <td className="p-3 text-foreground font-semibold">Yes</td>
                </tr>
                <tr className="border-b border-border bg-muted/20">
                  <td className="p-3 font-medium text-foreground">
                    FDA status of the 5-day protocol
                  </td>
                  <td className="p-3 text-foreground">n/a</td>
                  <td className="p-3 text-foreground">
                    Off-label (not tested in the SAINT\u00ae trials)
                  </td>
                  <td className="p-3 text-foreground font-semibold">
                    FDA-cleared
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium text-foreground">Device</td>
                  <td className="p-3 text-foreground">
                    Various cleared TMS devices
                  </td>
                  <td className="p-3 text-foreground">Various</td>
                  <td className="p-3 text-foreground">
                    Magnus Medical system only
                  </td>
                </tr>
                <tr className="border-b border-border bg-muted/20">
                  <td className="p-3 font-medium text-foreground">Ages</td>
                  <td className="p-3 text-foreground">
                    Adults (one device is cleared for ages 15\u201321)
                  </td>
                  <td className="p-3 text-foreground">
                    Varies, some advertise teens
                  </td>
                  <td className="p-3 text-foreground">
                    Adults [VERIFY current SAINT\u00ae age wording on the label]
                  </td>
                </tr>
                <tr>
                  <td className="p-3 font-medium text-foreground">
                    Published results
                  </td>
                  <td className="p-3 text-foreground">
                    Established for standard TMS
                  </td>
                  <td className="p-3 text-foreground">
                    Results from SAINT\u00ae trials don't transfer
                  </td>
                  <td className="p-3 text-foreground font-semibold">
                    79% remission in the controlled trial
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {P(
            <>
              <a
                href="/learn/saint-vs-accelerated-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                See the full SAINT\u00ae vs lower-cost \u201caccelerated
                TMS\u201d comparison
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Is accelerated TMS the same as SAINT?",
      body: (
        <>
          <AnswerBlock>{a_accel}</AnswerBlock>
          {P(
            "A 5-day, 10-sessions-a-day schedule does not make a treatment SAINT\u00ae. SAINT\u00ae is defined by three elements: the Magnus Neuromodulation System, a brain MRI with FDA-cleared targeting software that personalizes the target from your own functional-connectivity data, and delivery by a provider listed on Magnus Medical's official provider locator.",
          )}
          {P(
            "Some 5-day programs use scalp or head measurements (for example the \u201cBEAM F3\u201d method) instead of a brain MRI. They may copy the schedule, but they don't use the FDA-cleared targeting software or the Magnus device, and their own providers describe them as off-label and untested in the SAINT\u00ae clinical trials.",
          )}
          {P(
            <>
              Learn more in our detailed{" "}
              <a
                href="/learn/saint-vs-accelerated-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT\u00ae vs accelerated TMS comparison
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Is SAINT better than regular TMS?",
      body: (
        <>
          <AnswerBlock>{a_better}</AnswerBlock>
          {P(
            "No head-to-head trial has compared SAINT\u00ae and standard TMS directly, so no one can claim one is universally \u201cbetter.\u201d What we can say: SAINT\u00ae offers a faster five-day schedule and a stimulation target personalized from your own brain MRI. Standard TMS has a larger evidence base built over 20+ years and broader insurance coverage.",
          )}
          {P(
            "In the 2022 double-blind, sham-controlled randomized trial, 79% of the active-treatment group reached remission (Cole et al., Am J Psychiatry 2022;179(2):132\u2013141). Individual results vary, and no outcome is guaranteed. Suitability depends on your diagnosis, treatment history, and safety screening \u2014 your psychiatrist determines which is right for you.",
          )}
          {P(
            <>
              Check whether SAINT\u00ae is a fit for you:{" "}
              <a
                href="/am-i-a-candidate"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Am I a Candidate?
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Does SAINT\u00ae require an MRI?",
      body: (
        <>
          {P(
            "Yes. Every SAINT\u00ae patient has a brain MRI before treatment. Software uses the scan's functional-connectivity data to pick the exact spot to stimulate for that person. A clinic that offers a 5-day course without a brain scan isn't delivering SAINT\u00ae.",
          )}
          {P(
            <>
              Learn more:{" "}
              <a
                href="/learn/does-saint-tms-require-an-mri"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Does SAINT\u00ae require an MRI?
              </a>
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Insurance and cost",
      body: (
        <>
          {P(
            "Standard TMS is broadly covered by commercial insurance, Medicare, and the VA when criteria are met. SAINT\u00ae coverage is currently limited and decided case by case through prior authorization. We verify your benefits for free \u2014 we never say \u201ccovered by insurance\u201d about SAINT\u00ae because coverage varies by plan. Self-pay is available, and SAINT\u00ae at Neuro Wellness starts at $25,000 for the full five-day course, including the brain MRI and all 50 sessions.",
          )}
          {P(
            <>
              See full pricing and what's included:{" "}
              <a
                href="/learn/saint-tms-cost"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT\u00ae TMS Cost
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Who may be evaluated",
      body: (
        <>
          {P(
            "Both SAINT\u00ae and standard TMS are cleared for adults with major depressive disorder who have not improved on prior antidepressant medication. Eligibility requires a psychiatric evaluation and safety screening. People with non-removable magnetic-sensitive metal in or near the head, implanted devices, or who cannot safely have an MRI (required for SAINT\u00ae) are generally not candidates. Your psychiatrist determines which may be appropriate.",
          )}
        </>
      ),
    },
    {
      heading: "Suitability depends on individual circumstances",
      body: (
        <>
          {P(
            "Neither treatment is universally \u201cbetter.\u201d SAINT\u00ae may suit patients who cannot commit to six weeks or who want a target personalized from their own brain MRI. Standard TMS may suit patients whose insurance covers it or who prefer a larger evidence base. Neuro Wellness offers both. Individual results vary, and no outcome is guaranteed. Discuss with your psychiatrist which is right for you.",
          )}
          {P(
            <>
              To request a consultation,{" "}
              <a
                href="/contactus"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                contact our team
              </a>{" "}
              or call 954-827-9793.
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "O'Reardon JP, et al. Efficacy and safety of transcranial magnetic stimulation in the acute treatment of major depression. Biol Psychiatry. 2007;62(11):1208-1216.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17573064/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification \u2014 Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/saint-vs-traditional-tms#webpage`,
        name: "SAINT\u00ae vs TMS: What's the Difference?",
        url: `${SITE_URL}/learn/saint-vs-traditional-tms`,
        description:
          "SAINT\u00ae vs regular TMS and 5-day accelerated TMS: how targeting, schedule, device, age limits, results and cost really differ.",
        about: [
          {
            "@type": "MedicalTherapy",
            name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT\u00ae)",
          },
          {
            "@type": "MedicalTherapy",
            name: "Transcranial Magnetic Stimulation (TMS)",
          },
        ],
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/saint-vs-traditional-tms#faq`),
      breadcrumbJsonld(baseCrumbs("SAINT\u00ae vs TMS")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "fMRI-Guided TMS for Depression",
      href: "/learn/fmri-guided-tms-for-depression",
      description: "How personalized targeting works",
    },
    {
      title: "SAINT\u00ae vs Lower-Cost Accelerated TMS",
      href: "/learn/saint-vs-accelerated-tms",
      description: "What's the same, what isn't, and what to ask",
    },
    {
      title: "Does SAINT\u00ae Require an MRI?",
      href: "/learn/does-saint-tms-require-an-mri",
      description: "Why the brain scan is part of the protocol",
    },
  ],
};
